/**
 * chunkRecoveryPolicy.ts
 *
 * Shared chunk-failure recovery logic used by both lazyWithRetry (routes.tsx)
 * and ErrorBoundary.tsx. Single source of truth for keys, limits, and guards.
 *
 * MUST NOT import React or any heavy dependencies — loaded at module scope.
 */

// ── Storage keys ──
export const CHUNK_RELOAD_KEY = 'coretrak.chunkReload';
export const CHUNK_INFLIGHT_KEY = 'coretrak.chunkReload.inFlight';
export const CHUNK_TARGET_KEY = 'coretrak.chunkReload.target';
export const CHUNK_TOTAL_COUNT_KEY = 'coretrak.chunkReload.totalCount';

// ── Limits ──
export const CHUNK_MAX_RELOADS = 2;                    // Max reloads per window
export const CHUNK_WINDOW_MS = 24 * 60 * 60 * 1000;   // 24 hours
export const CHUNK_COOLDOWN_MS = 10_000;               // Min gap between reloads
export const CHUNK_INFLIGHT_TTL_MS = 10_000;           // In-flight guard TTL

// ── Guard state shape ──
interface ChunkReloadState {
  lastReloadAt: number;
  count: number;
  windowStart: number;
}

/**
 * Check if an automatic chunk-failure reload is allowed.
 * If allowed, increments the counter and returns true.
 * Includes in-flight guard (sessionStorage) to prevent ping-pong.
 */
export function canAutoReloadForChunk(): boolean {
  try {
    // In-flight guard: if a reload was triggered < TTL ago, don't reload again
    const inFlight = sessionStorage.getItem(CHUNK_INFLIGHT_KEY);
    if (inFlight && Date.now() - parseInt(inFlight, 10) < CHUNK_INFLIGHT_TTL_MS) {
      return false;
    }

    const raw = localStorage.getItem(CHUNK_RELOAD_KEY);
    const now = Date.now();
    let state: ChunkReloadState = raw
      ? JSON.parse(raw)
      : { lastReloadAt: 0, count: 0, windowStart: now };

    // Reset window if expired
    if (now - state.windowStart > CHUNK_WINDOW_MS) {
      state = { lastReloadAt: 0, count: 0, windowStart: now };
    }

    // Check cooldown and max count
    if (now - state.lastReloadAt < CHUNK_COOLDOWN_MS) return false;
    if (state.count >= CHUNK_MAX_RELOADS) return false;

    // Record this reload
    state.lastReloadAt = now;
    state.count += 1;
    localStorage.setItem(CHUNK_RELOAD_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false; // If storage fails, don't auto-reload
  }
}

/**
 * Check if an error is a chunk/module load failure.
 */
export function isChunkLoadError(error: any): boolean {
  const msg = (error?.message || '').toLowerCase();
  return (
    error?.name === 'ChunkLoadError' ||
    msg.includes('loading chunk') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

/**
 * Store the target route + mark in-flight before triggering a reload.
 */
export function markChunkReloadInFlight(): void {
  try {
    const target = window.location.pathname + window.location.search + window.location.hash;
    sessionStorage.setItem(CHUNK_TARGET_KEY, target);
    sessionStorage.setItem(CHUNK_INFLIGHT_KEY, Date.now().toString());
  } catch { /* storage full — proceed anyway */ }
}

/**
 * Build the cache-busted reload URL, preserving the original route for restoration.
 */
export function buildChunkReloadUrl(): string {
  const target = window.location.pathname + window.location.search + window.location.hash;
  return '/?_v=' + Date.now() + '&r=' + encodeURIComponent(target);
}

/**
 * Log a telemetry breadcrumb for chunk recovery (no Sentry dependency).
 */
export function logChunkRecoveryBreadcrumb(source: 'lazyWithRetry' | 'ErrorBoundary'): void {
  try {
    const target = window.location.pathname + window.location.search + window.location.hash;
    const buildVersion = (import.meta as any).env?.VITE_BUILD_VERSION || 'unknown';
    console.log(`[ChunkRecovery] ${source} auto-reload fired { route: "${target}", buildVersion: "${buildVersion}" }`);
    const counter = parseInt(localStorage.getItem(CHUNK_TOTAL_COUNT_KEY) || '0', 10);
    localStorage.setItem(CHUNK_TOTAL_COUNT_KEY, String(counter + 1));
  } catch { /* ignore */ }
}

/**
 * Get current build version (best-effort).
 */
export function getBuildVersion(): string {
  try {
    return (import.meta as any).env?.VITE_BUILD_VERSION
      || (import.meta as any).env?.VERCEL_GIT_COMMIT_SHA?.slice(0, 8)
      || 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Validate a target path is safe for internal navigation (no open redirect).
 */
export function isSafeInternalTarget(t: string): boolean {
  return typeof t === 'string' && t.startsWith('/') && !t.startsWith('//') && !t.includes('://');
}
